import {
  Bar, BarChart, CartesianGrid, Cell, Label, LabelList, Pie, PieChart, XAxis, YAxis,
} from 'recharts';
import { ArrowRight, CircleGauge, Route, ShieldCheck, Truck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  ChartContainer, ChartLegend, ChartTooltip, ChartTooltipContent,
} from './ui/chart';
import { Button } from './ui/button';
import { formatNumber } from '../lib/utils';

const palette = {
  teal: '#0f766e',
  cyan: '#0891b2',
  amber: '#d97706',
  orange: '#ea580c',
  slate: '#94a3b8',
  navy: '#0b6e99',
};

export const buildReportChartData = (report = {}) => {
  const rows = report.rows || [];
  const activity = report.activitySummary || {};
  const summary = report.summary || {};
  const tyreCount = Number(summary.tyreCount ?? rows.length ?? 0);
  const inventory = Number(summary.inventoryCount || 0);
  const installed = Number(summary.installedCount || 0);
  const other = Math.max(tyreCount - inventory - installed, 0);

  const conditionCounts = rows.reduce((counts, row) => {
    const raw = String(row.condition || 'unknown').toLowerCase();
    const key = ['new', 'good', 'fair', 'worn', 'damaged'].includes(raw) ? raw : 'unknown';
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});
  const conditionOrder = ['new', 'good', 'fair', 'worn', 'damaged', 'unknown'];
  const conditionColors = [palette.teal, palette.cyan, palette.amber, palette.orange, '#dc2626', palette.slate];
  const health = conditionOrder
    .filter((key) => conditionCounts[key])
    .map((key) => ({
      key,
      label: key === 'unknown' ? 'Unclassified' : `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
      value: conditionCounts[key],
      fill: conditionColors[conditionOrder.indexOf(key)],
    }));

  return {
    deployment: [
      { key: 'installed', label: 'On vehicles', value: installed, fill: palette.teal },
      { key: 'inventory', label: 'In inventory', value: inventory, fill: palette.amber },
      ...(other ? [{ key: 'other', label: 'Other custody', value: other, fill: palette.slate }] : []),
    ].filter((item) => item.value > 0),
    lifecycle: [{
      label: 'Installations',
      firstLife: Number(activity.newInstallations || 0),
      retreaded: Number(activity.retreadedInstallations || 0),
      unknown: Math.max(
        Number(activity.unknownCategoryInstallations || 0),
        Number(activity.installations || 0)
          - Number(activity.newInstallations || 0)
          - Number(activity.retreadedInstallations || 0),
        0
      ),
    }],
    health,
    vehicles: [...(report.vehicleRows || [])]
      .filter((row) => Number(row.installations || 0) > 0)
      .sort((a, b) => Number(b.installations || 0) - Number(a.installations || 0))
      .slice(0, 6)
      .map((row) => ({
        id: row.vehicleId,
        vehicle: row.vehicle,
        installations: Number(row.installations || 0),
        changes: Number(row.tyresChanged || 0),
      })),
  };
};

const EmptyChart = ({ children }) => (
  <div className="flex h-[170px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/70 px-5 text-center text-sm text-slate-500">{children}</div>
);

const ChartHeading = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-2.5">
    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-operational-100 text-operational-700"><Icon className="h-4 w-4" /></span>
    <div><CardTitle className="text-base">{title}</CardTitle><p className="mt-1 text-xs leading-5 text-slate-500">{description}</p></div>
  </div>
);

const DeploymentChart = ({ data, total }) => (
  <Card className="min-w-0 overflow-hidden">
    <CardHeader className="p-4 pb-1"><ChartHeading icon={CircleGauge} title="Fleet deployment" description="Where the current tyre population is held." /></CardHeader>
    <CardContent className="p-4 pt-1">
      {data.length ? <>
        <ChartContainer config={{ value: { label: 'Tyres' } }} className="mx-auto h-[170px] max-w-[280px]">
          <PieChart>
            <ChartTooltip content={<ChartTooltipContent hideLabel valueFormatter={(value) => formatNumber(value)} />} />
            <Pie isAnimationActive={false} data={data} dataKey="value" nameKey="label" innerRadius={54} outerRadius={74} paddingAngle={3} strokeWidth={0}>
              {data.map((item) => <Cell key={item.key} fill={item.fill} />)}
              <Label content={({ viewBox }) => viewBox?.cx && viewBox?.cy ? (
                <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                  <tspan x={viewBox.cx} y={viewBox.cy - 4} className="fill-slate-950 text-3xl font-extrabold">{formatNumber(total)}</tspan>
                  <tspan x={viewBox.cx} y={viewBox.cy + 20} className="fill-slate-500 text-[11px] font-semibold">tracked tyres</tspan>
                </text>
              ) : null} />
            </Pie>
          </PieChart>
        </ChartContainer>
        <ChartLegend className="justify-center" items={data.map((item) => ({ label: `${item.label} · ${formatNumber(item.value)}`, color: item.fill }))} />
      </> : <EmptyChart>No tyre deployment data for this selection.</EmptyChart>}
    </CardContent>
  </Card>
);

const LifecycleChart = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader className="p-4 pb-1"><ChartHeading icon={Route} title="Installation lifecycle" description="First-life and retread usage in the selected period." /></CardHeader>
      <CardContent className="p-4 pt-1">
        {total ? <>
          <ChartContainer config={{ value: { label: 'Installations' } }} className="mx-auto h-[170px] max-w-[280px]">
            <PieChart>
              <ChartTooltip content={<ChartTooltipContent hideLabel valueFormatter={(value) => formatNumber(value)} />} />
              <Pie isAnimationActive={false} data={data} dataKey="value" nameKey="label" innerRadius={42} outerRadius={74} paddingAngle={2} strokeWidth={0}>
                {data.map((item) => <Cell key={item.key} fill={item.fill} />)}
              </Pie>
            </PieChart>
          </ChartContainer>
          <ChartLegend className="justify-center" items={data.map((item) => ({ label: `${item.label} · ${formatNumber(item.value)} (${Math.round(item.value * 100 / total)}%)`, color: item.fill }))} />
        </> : <EmptyChart>No installations recorded in this period.</EmptyChart>}
      </CardContent>
    </Card>
  );
};

const ConditionChart = ({ data }) => (
  <Card className="min-w-0 overflow-hidden">
    <CardHeader className="p-4 pb-1"><ChartHeading icon={ShieldCheck} title="Condition profile" description="Current condition recorded across matching tyres." /></CardHeader>
    <CardContent className="p-4 pt-1">
      {data.length ? <>
        <ChartContainer config={{ value: { label: 'Tyres' } }} className="h-[170px] w-full" aria-label="Tyres grouped by current condition">
          <BarChart data={data} margin={{ left: 8, right: 4, top: 14, bottom: 0 }}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 600 }} interval={0} />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={48} tickFormatter={(value) => formatNumber(value)} />
            <ChartTooltip cursor={{ fill: '#f1f5f9' }} content={<ChartTooltipContent hideLabel valueFormatter={(value) => formatNumber(value)} />} />
            <Bar isAnimationActive={false} dataKey="value" radius={[5, 5, 0, 0]}>
              {data.map((item) => <Cell key={item.key} fill={item.fill} />)}
              <LabelList dataKey="value" position="top" className="fill-slate-700 font-mono text-[10px] font-bold" />
            </Bar>
          </BarChart>
        </ChartContainer>
        <ul className="mt-3 flex flex-wrap justify-center gap-3 text-xs text-slate-600" aria-label="Condition values">{data.map((item) => <li key={item.key}>{item.label}: {formatNumber(item.value)}</li>)}</ul>
        <p className="mt-2 text-center text-xs text-slate-500">Based on the latest condition for {formatNumber(data.reduce((sum, item) => sum + item.value, 0))} matching tyres</p>
      </> : <EmptyChart>No condition data for matching tyres.</EmptyChart>}
    </CardContent>
  </Card>
);

const VehicleActivityChart = ({ data, onVehicleSelect }) => (
  <Card className="min-w-0 overflow-hidden lg:col-span-2">
    <CardHeader className="p-4 pb-1"><ChartHeading icon={Truck} title="Vehicle tyre activity" description="The busiest vehicles by installations, with completed changes alongside." /></CardHeader>
    <CardContent className="p-4 pt-1">
      {data.length ? <>
        <ChartContainer config={{ installations: { label: 'Installations' }, changes: { label: 'Tyres changed' } }} className="h-[280px] w-full">
          <BarChart data={data} layout="vertical" margin={{ left: 4, right: 12, top: 8, bottom: 0 }} barCategoryGap={9}>
            <CartesianGrid horizontal={false} />
            <XAxis type="number" allowDecimals={false} axisLine={false} tickLine={false} />
            <YAxis dataKey="vehicle" type="category" width={92} axisLine={false} tickLine={false} tick={{ fontSize: 11, fontWeight: 600 }} />
            <ChartTooltip cursor={{ fill: '#f1f5f9' }} content={<ChartTooltipContent valueFormatter={(value) => formatNumber(value)} />} />
            <Bar isAnimationActive={false} dataKey="installations" fill={palette.navy} radius={[0, 4, 4, 0]} />
            <Bar isAnimationActive={false} dataKey="changes" fill={palette.amber} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ChartContainer>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <ChartLegend items={[{ label: 'Installations', color: palette.navy }, { label: 'Tyres changed', color: palette.amber }]} />
          <ul className="w-full divide-y divide-slate-100" aria-label="Vehicle activity values">
            {data.map((item) => <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <Button size="sm" variant="ghost" aria-label={`View ${item.vehicle} history`} onClick={() => onVehicleSelect(item.id)}>{item.vehicle}<ArrowRight className="h-4 w-4" /></Button>
              <span className="text-xs tabular-nums text-slate-600">{formatNumber(item.installations)} installations · {formatNumber(item.changes)} tyres changed</span>
            </li>)}
          </ul>
        </div>
      </> : <EmptyChart>No vehicle installation activity in this period.</EmptyChart>}
    </CardContent>
  </Card>
);

export const TyreReportCharts = ({ report, onVehicleSelect, group = 'activity' }) => {
  const data = buildReportChartData(report);
  const lifecycle = [
    { key: 'firstLife', label: 'First life', value: data.lifecycle[0].firstLife, fill: palette.cyan },
    { key: 'retreaded', label: 'Retreaded', value: data.lifecycle[0].retreaded, fill: palette.teal },
    { key: 'unknown', label: 'Unknown', value: data.lifecycle[0].unknown, fill: palette.slate },
  ].filter((item) => item.value > 0);
  const total = Number(report.summary?.tyreCount ?? (report.rows || []).length);
  return group === 'fleet' ? (
    <div className="grid gap-5 md:grid-cols-2">
      <DeploymentChart data={data.deployment} total={total} />
      <ConditionChart data={data.health} />
    </div>
  ) : (
    <div className="grid items-start gap-5 lg:grid-cols-3">
      <VehicleActivityChart data={data.vehicles} onVehicleSelect={onVehicleSelect} />
      <LifecycleChart data={lifecycle} />
    </div>
  );
};
export default TyreReportCharts;
